import React from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiClose, mdiEmailOutline } from '@mdi/js';
import { RecommendationResult } from '../types';

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  width: 560px;
  max-height: 480px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e8eaed;
`;

const HeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #202124;
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #5f6368;
  border-radius: 4px;

  &:hover {
    background: #f1f3f4;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const Th = styled.th`
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #5f6368;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaed;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
  }
`;

const Td = styled.td`
  padding: 8px 12px;
  color: #202124;
  border-bottom: 1px solid #f1f3f4;
  white-space: nowrap;
`;

const StatusDot = styled.span<{ status: 'suitable' | 'marginal' | 'insufficient' }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${props => {
    switch (props.status) {
      case 'suitable': return '#34a853';
      case 'marginal': return '#fbbc04';
      case 'insufficient': return '#ea4335';
    }
  }};
`;

const StatusText = styled.span<{ status: 'suitable' | 'marginal' | 'insufficient' }>`
  color: ${props => {
    switch (props.status) {
      case 'suitable': return '#137333';
      case 'marginal': return '#e37400';
      case 'insufficient': return '#c5221f';
    }
  }};
  font-weight: 500;
`;

const MarginValue = styled.span<{ $positive: boolean }>`
  color: ${props => props.$positive ? '#137333' : '#c5221f'};
  font-weight: 500;
`;

const QuoteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 10px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  border: none;
  background: #202D61;
  color: #fff;

  &:hover {
    background: #1a2450;
  }
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #9aa0a6;
  font-size: 13px;
`;

interface RecommendationTableProps {
  recommendations: RecommendationResult[];
  onSelect: (recommendation: RecommendationResult) => void;
  onClose: () => void;
  onRequestQuote?: () => void;
}

export const RecommendationTable: React.FC<RecommendationTableProps> = ({
  recommendations,
  onSelect,
  onClose,
  onRequestQuote,
}) => {
  const sorted = [...recommendations].sort((a, b) => {
    const statusOrder = { suitable: 0, marginal: 1, insufficient: 2 };
    const sDiff = statusOrder[a.status] - statusOrder[b.status];
    if (sDiff !== 0) return sDiff;
    return b.worst_margin - a.worst_margin;
  }).slice(0, 3);

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderTitle>Top 3 Cooling Package Recommendations</HeaderTitle>
          <CloseButton onClick={onClose}>
            <Icon path={mdiClose} size={0.7} />
          </CloseButton>
        </Header>

        <TableContainer>
          {sorted.length === 0 ? (
            <EmptyState>No recommendations available.</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Status</Th>
                  <Th>Cooling Package</Th>
                  <Th>Margin</Th>
                  <Th>Worst Scenario</Th>
                  {onRequestQuote && <Th></Th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((rec) => (
                  <Tr key={rec.component_id} onClick={() => onSelect(rec)}>
                    <Td>
                      <StatusDot status={rec.status} />
                      <StatusText status={rec.status}>
                        {rec.status.charAt(0).toUpperCase() + rec.status.slice(1)}
                      </StatusText>
                    </Td>
                    <Td>{rec.component_name}</Td>
                    <Td>
                      <MarginValue $positive={rec.worst_margin >= 0}>
                        {rec.worst_margin >= 0 ? '+' : ''}{rec.worst_margin.toFixed(1)} °C
                      </MarginValue>
                    </Td>
                    <Td>{rec.worst_scenario}</Td>
                    {onRequestQuote && (
                      <Td>
                        <QuoteButton onClick={(e) => { e.stopPropagation(); onRequestQuote(); }}>
                          <Icon path={mdiEmailOutline} size={0.4} />
                          Quote
                        </QuoteButton>
                      </Td>
                    )}
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableContainer>
      </Panel>
    </Overlay>
  );
};
